import type { GroupRoundGame, GroupRoundPlayer, SimplifiedPayment } from '@/lib/db/types';
import type { CourseHole } from '@/lib/db/types';
import { findCatalogEntry } from '@/lib/games/game-catalog';
import { getGamePlugin } from './registry';
import { settleAllPresses } from './presses';
import { stakeFromDbRow } from './stakes-helpers';
import type { GameInstanceConfig, GameResult, RoundContext, RoundPlayer, StakesConfig } from './types';

export function scoresMatrixFromDb(
  players: GroupRoundPlayer[],
  scores: Array<{ player_key: string; hole_num: number; strokes: number | null }>,
  holeCount = 18,
): Record<string, (number | null)[]> {
  const matrix: Record<string, (number | null)[]> = {};
  players.forEach((p) => { matrix[p.player_key] = Array(holeCount).fill(null); });
  scores.forEach((s) => {
    if (!matrix[s.player_key]) matrix[s.player_key] = Array(holeCount).fill(null);
    matrix[s.player_key][s.hole_num - 1] = s.strokes;
  });
  return matrix;
}

export function puttsMatrixFromDb(
  players: GroupRoundPlayer[],
  scores: Array<{ player_key: string; hole_num: number; putts?: number | null }>,
  holeCount = 18,
): Record<string, (number | null)[]> {
  const matrix: Record<string, (number | null)[]> = {};
  players.forEach((p) => { matrix[p.player_key] = Array(holeCount).fill(null); });
  scores.forEach((s) => {
    if (!matrix[s.player_key]) matrix[s.player_key] = Array(holeCount).fill(null);
    if (s.putts != null) matrix[s.player_key][s.hole_num - 1] = s.putts;
  });
  return matrix;
}

export function buildRoundPlayers(
  players: GroupRoundPlayer[],
  organizerMemberId: string,
): RoundPlayer[] {
  return players.map((p) => ({
    playerKey: p.player_key,
    id: p.member_id || p.player_key,
    name: p.display_name,
    you: p.member_id === organizerMemberId,
    handicap: p.handicap,
    courseHcp: p.course_handicap,
    teamIndex: p.team_index,
  }));
}

export function stakesFromGames(games: GroupRoundGame[]): StakesConfig {
  const stakes: StakesConfig = {};
  games.filter((g) => g.enabled).forEach((g) => {
    stakes[g.format_id] = stakeFromDbRow(g.format_id, (g.stakes || {}) as Record<string, unknown>);
  });
  return stakes;
}

function buildGameConfig(g: GroupRoundGame, teams?: string[][]): GameInstanceConfig {
  const config = (g.config || {}) as Record<string, unknown>;
  const entry = findCatalogEntry(g.format_id);
  const base: GameInstanceConfig = {
    opponentKey: config.opponentKey as string | undefined,
    partnerKey: config.partnerKey as string | undefined,
    playerKeys: config.playerKeys as string[] | undefined,
    matches: config.matches as GameInstanceConfig['matches'],
    teams,
    holeState: config.holeState as GameInstanceConfig['holeState'],
    dotsEnabled: config.dotsEnabled as string[] | undefined,
    teamScores: config.teamScores as Record<string, (number | null)[]>,
    loneWolfMultiplier: config.loneWolfMultiplier as number | undefined,
    presses: config.presses as GameInstanceConfig['presses'],
  };
  if (g.format_id === 'lone-wolf') {
    base.wolfMode = 'lone-only';
  }
  if (entry?.id === 'better-ball') {
    // alias — same plugin as best-ball
  }
  return base;
}

export function computeAllGames(
  holes: CourseHole[],
  players: RoundPlayer[],
  scores: Record<string, (number | null)[]>,
  games: GroupRoundGame[],
  teams?: string[][],
  putts?: Record<string, (number | null)[]>,
): GameResult[] {
  const stakes = stakesFromGames(games);
  const ctx: RoundContext = { holes, players, scores, putts, stakes, teams };
  const results: GameResult[] = [];

  games.filter((g) => g.enabled).forEach((g) => {
    const plugin = getGamePlugin(g.format_id);
    if (!plugin) return;
    const config = buildGameConfig(g, teams);
    const result = plugin.compute(ctx, config);
    const pressSettlement = settleAllPresses(g, ctx, config);
    const mergedNet: Record<string, number> = { ...result.netOwed };
    Object.entries(pressSettlement.netOwed).forEach(([k, v]) => {
      mergedNet[k] = (mergedNet[k] || 0) + v;
    });
    results.push({
      ...result,
      formatId: g.format_id,
      netOwed: mergedNet,
      detail: {
        ...(result.detail || {}),
        pressLines: pressSettlement.lines,
        presses: pressSettlement.presses,
      },
    });
  });

  return results;
}

export function aggregateNetOwed(results: GameResult[]): Record<string, number> {
  const net: Record<string, number> = {};
  results.forEach((r) => {
    Object.entries(r.netOwed).forEach(([k, v]) => {
      net[k] = (net[k] || 0) + v;
    });
  });
  return net;
}

/** Greedy minimum-cash-flow settlement. Amounts in cents. */
export function minimizePayments(netByPlayer: Record<string, number>): SimplifiedPayment[] {
  const entries = Object.entries(netByPlayer)
    .map(([player_key, cents]) => ({ player_key, cents: Math.round(cents) }))
    .filter((e) => e.cents !== 0);

  const debtors = entries.filter((e) => e.cents < 0).map((e) => ({ ...e, cents: -e.cents })).sort((a, b) => b.cents - a.cents);
  const creditors = entries.filter((e) => e.cents > 0).sort((a, b) => b.cents - a.cents);
  const payments: SimplifiedPayment[] = [];

  let i = 0;
  let j = 0;
  while (i < debtors.length && j < creditors.length) {
    const pay = Math.min(debtors[i].cents, creditors[j].cents);
    if (pay > 0) {
      payments.push({
        from_player_key: debtors[i].player_key,
        to_player_key: creditors[j].player_key,
        amount_cents: pay,
      });
    }
    debtors[i].cents -= pay;
    creditors[j].cents -= pay;
    if (debtors[i].cents === 0) i++;
    if (creditors[j].cents === 0) j++;
  }
  return payments;
}

export function computeRoundSettlement(
  holes: CourseHole[],
  dbPlayers: GroupRoundPlayer[],
  scores: Array<{ player_key: string; hole_num: number; strokes: number | null; putts?: number | null }>,
  games: GroupRoundGame[],
  organizerMemberId: string,
  teams?: string[][],
) {
  const players = buildRoundPlayers(dbPlayers, organizerMemberId);
  const matrix = scoresMatrixFromDb(dbPlayers, scores);
  const puttsMatrix = puttsMatrixFromDb(dbPlayers, scores);
  const gameResults = computeAllGames(holes, players, matrix, games, teams, puttsMatrix);
  const net_by_player = aggregateNetOwed(gameResults);
  const simplified_payments = minimizePayments(net_by_player);
  return { gameResults, net_by_player, simplified_payments };
}
